import { Card, Muted, RadialMeter, Stack, color, space, status } from '@biopeak/ui';
import { useState } from 'react';
import {
  DOMINIOS,
  formatarData,
  useSerbioColetas,
  useSerbioIndices,
  type SerbioIndice,
} from '../lib/serbio.ts';

/**
 * Painel SERBIO (protocolo Dr. Serfaty, 06/08/2026).
 *
 * Aba própria e não card na visão geral: são 15 índices, um score composto e
 * duas listas de gatilhos. Espremer isso entre os cards de sono e VO2 max
 * viraria o painel de instrumentos que o §8.4 pede para evitar. A visão geral
 * recebe só o CVRC — o número que responde "preciso abrir isso agora?".
 *
 * A tela não classifica nada: cor e faixa chegam prontas de `serbio_indices`.
 */
export function SerbioPanel({ patientId }: { patientId: string }) {
  const coletas = useSerbioColetas(patientId);
  const indices = useSerbioIndices(patientId);
  const [dataEscolhida, setDataEscolhida] = useState<string | null>(null);

  if (coletas.isLoading) return <Muted>Carregando…</Muted>;

  const lista = coletas.data ?? [];
  const maisRecente = lista[0];
  if (!maisRecente) {
    return (
      <Card>
        <Stack gap={space.sm}>
          <span style={{ fontSize: '17px', fontWeight: 600 }}>Painel SERBIO</span>
          <Muted style={{ fontSize: '14px' }}>
            Nenhum índice pôde ser calculado ainda. O painel aparece quando houver
            exame com os analitos do protocolo confirmados na revisão — hemograma,
            perfil lipídico, glicemia e PCR são os que destravam a maior parte.
          </Muted>
        </Stack>
      </Card>
    );
  }

  const coleta = lista.find((c) => c.data_coleta === dataEscolhida) ?? maisRecente;
  const doDia = (indices.data ?? []).filter((i) => i.data_coleta === coleta.data_coleta);

  return (
    <Stack gap={space.md}>
      <ResumoCvrc
        coleta={coleta}
        coletas={lista}
        onEscolher={setDataEscolhida}
      />

      <Gatilhos coleta={coleta} />

      {DOMINIOS.map((dominio) => {
        const doDominio = doDia.filter((i) => i.dominio === dominio);
        if (doDominio.length === 0) return null;
        return <GrupoDominio key={dominio} dominio={dominio} indices={doDominio} />;
      })}

      <Muted style={{ fontSize: '12px' }}>
        Índices calculados sobre valores já confirmados por você na revisão de
        exames, em unidade padronizada. Protocolo SERBIO — Serfaty Clínicas,
        06/08/2026. Estratificação de risco não substitui avaliação clínica.
      </Muted>
    </Stack>
  );
}

// -------------------------------------------------------------- CVRC

type Coleta = ReturnType<typeof useSerbioColetas>['data'] extends (infer T)[] | undefined
  ? T
  : never;

/**
 * O CVRC ganha a cor pela faixa do próprio protocolo: 0–2 baixo, 3–5
 * intermediário, 6–10 alto.
 */
function tomDoCvrc(cvrc: number) {
  if (cvrc >= 6) return status.red;
  if (cvrc >= 3) return status.yellow;
  return status.green;
}

function ResumoCvrc({
  coleta,
  coletas,
  onEscolher,
}: {
  coleta: Coleta;
  coletas: Coleta[];
  onEscolher: (data: string) => void;
}) {
  const cvrc = coleta.cvrc ?? 0;
  const avaliados = coleta.cvrc_criterios_avaliados ?? 0;
  const tom = tomDoCvrc(cvrc);

  return (
    <Card>
      <Stack gap={space.md}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: space.sm,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '17px', fontWeight: 600 }}>
            SERBIO-CVRC — risco cardiovascular cumulativo
          </span>
          {coletas.length > 1 && (
            <select
              value={coleta.data_coleta ?? ''}
              onChange={(e) => onEscolher(e.target.value)}
              style={{
                fontSize: '13px',
                padding: '4px 8px',
                borderRadius: '6px',
                border: `1px solid ${color.border}`,
                background: 'transparent',
                color: color.text,
              }}
            >
              {coletas.map((c) => (
                <option key={c.data_coleta} value={c.data_coleta ?? ''}>
                  Coleta de {formatarData(c.data_coleta)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space.lg,
            flexWrap: 'wrap',
          }}
        >
          <RadialMeter
            value={cvrc}
            min={0}
            max={10}
            size={150}
            fillColor={tom.fg}
            valueLabel={String(cvrc)}
            unitLabel="de 10"
            startEndpointLabel="0"
            endEndpointLabel="10"
          />

          <Stack gap={space.xs} style={{ flex: 1, minWidth: '240px' }}>
            <span style={{ fontSize: '15px', fontWeight: 600, color: tom.fg }}>
              {cvrc >= 6 ? 'Risco alto' : cvrc >= 3 ? 'Risco intermediário' : 'Risco baixo'}
            </span>
            <Muted style={{ fontSize: '13px' }}>
              Coleta de {formatarData(coleta.data_coleta)}.
            </Muted>

            {/* Sem esta linha o score mentiria por omissão: critério não
                dosado pontua zero, e um paciente pouco investigado fica
                indistinguível de um paciente saudável. */}
            {avaliados < 8 && (
              <div
                style={{
                  background: status.yellow.bg,
                  border: `1px solid ${status.yellow.border}`,
                  borderRadius: '6px',
                  padding: space.sm,
                  fontSize: '13px',
                  color: status.yellow.fg,
                }}
              >
                <strong>{avaliados} de 8 critérios</strong> tinham dado nesta coleta. Os
                {' '}{8 - avaliados} restantes contam como zero por falta de exame, não
                por estarem normais — o score real pode ser mais alto.
              </div>
            )}
          </Stack>
        </div>

        <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
          <Composto rotulo="SERBIO-I3" valor={coleta.serbio_i3} legenda="inflamação × insulina" />
          <Composto rotulo="SERBIO-AC" valor={coleta.serbio_ac} legenda="aterogênico × inflamatório" />
          <Composto rotulo="IMC" valor={coleta.imc} legenda="kg/m²" />
          <Composto rotulo="WHtR" valor={coleta.whtr} legenda="cintura/altura" />
          <Composto rotulo="Pressão de pulso" valor={coleta.pp} legenda="mmHg" />
        </div>
      </Stack>
    </Card>
  );
}

function Composto({
  rotulo,
  valor,
  legenda,
}: {
  rotulo: string;
  valor: number | null;
  legenda: string;
}) {
  return (
    <Stack gap="2px">
      <span style={{ fontSize: '12px', color: color.textSecondary }}>{rotulo}</span>
      <span style={{ fontSize: '20px', fontWeight: 600 }}>
        {valor ?? '—'}
      </span>
      <span style={{ fontSize: '11px', color: color.textSecondary }}>{legenda}</span>
    </Stack>
  );
}

// ---------------------------------------------------------- gatilhos

function Gatilhos({ coleta }: { coleta: Coleta }) {
  const glp1 = coleta.gatilhos_glp1 ?? [];
  const vascular = coleta.gatilhos_vasculo_renais ?? [];
  if (glp1.length === 0 && vascular.length === 0) return null;

  return (
    <Card>
      <Stack gap={space.md}>
        <span style={{ fontSize: '17px', fontWeight: 600 }}>
          Critérios de intensificação atingidos
        </span>

        {glp1.length > 0 && (
          <ListaGatilho
            titulo="Agonistas de GLP-1/GIP (§5.1)"
            itens={glp1}
          />
        )}
        {vascular.length > 0 && (
          <ListaGatilho
            titulo="Proteção vascular e renal (§5.2)"
            itens={vascular}
          />
        )}

        <Muted style={{ fontSize: '12px' }}>
          São os critérios do protocolo que este exame atingiu — não uma
          prescrição. A indicação, as contraindicações e o momento são decisão
          sua, com o paciente à frente.
        </Muted>
      </Stack>
    </Card>
  );
}

function ListaGatilho({ titulo, itens }: { titulo: string; itens: string[] }) {
  return (
    <Stack gap={space.xs}>
      <span style={{ fontSize: '14px', fontWeight: 600 }}>{titulo}</span>
      {itens.map((item) => (
        <div
          key={item}
          style={{
            borderLeft: `3px solid ${status.yellow.border}`,
            background: status.yellow.bg,
            borderRadius: '4px',
            padding: `${space.xs} ${space.sm}`,
            fontSize: '13px',
            color: status.yellow.fg,
          }}
        >
          {item}
        </div>
      ))}
    </Stack>
  );
}

// ------------------------------------------------------------ índices

const TOM_SITUACAO: Record<string, { fg: string; bg: string; border: string }> = {
  alto: status.red,
  atencao: status.yellow,
  normal: status.green,
};

const ROTULO_SITUACAO: Record<string, string> = {
  alto: 'alto',
  atencao: 'atenção',
  normal: 'normal',
  indefinido: 'informe o sexo',
};

function GrupoDominio({
  dominio,
  indices,
}: {
  dominio: string;
  indices: SerbioIndice[];
}) {
  return (
    <Card>
      <Stack gap={space.sm}>
        <span style={{ fontSize: '15px', fontWeight: 600 }}>{dominio}</span>
        {indices.map((i) => (
          <Indice key={i.chave} indice={i} />
        ))}
      </Stack>
    </Card>
  );
}

function Indice({ indice }: { indice: SerbioIndice }) {
  const situacao = indice.situacao ?? 'normal';
  // `indefinido` não recebe cor de julgamento de propósito: o valor está
  // certo, mas o corte depende do sexo biológico, que ainda não foi
  // informado. Pintar de amarelo seria afirmar um risco que não foi avaliado.
  const tom = TOM_SITUACAO[situacao];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: space.sm,
        flexWrap: 'wrap',
        borderLeft: `3px solid ${tom ? tom.border : color.border}`,
        background: tom && situacao !== 'normal' ? tom.bg : 'transparent',
        borderRadius: '4px',
        padding: `${space.xs} ${space.sm}`,
      }}
    >
      <Stack gap="2px" style={{ flex: 1, minWidth: '220px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600 }}>{indice.nome}</span>
        <span style={{ fontSize: '12px', color: color.textSecondary, lineHeight: 1.45 }}>
          {indice.interpretacao}
        </span>
      </Stack>

      <Stack gap="2px" style={{ textAlign: 'right' }}>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 600,
            color: tom && situacao !== 'normal' ? tom.fg : color.text,
          }}
        >
          {indice.valor}
        </span>
        <span style={{ fontSize: '11px', color: color.textSecondary }}>
          referência {indice.faixa}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: tom && situacao !== 'normal' ? tom.fg : color.textSecondary,
          }}
        >
          {ROTULO_SITUACAO[situacao] ?? situacao}
        </span>
      </Stack>
    </div>
  );
}
